"use client";


import { IconButton, Skeleton, colors, Typography } from "@mui/material";
import Box, { BoxProps } from "@mui/material/Box";
import TextOverflow from "@v2/components/styled/TextOverflow";
import { calcFromNow, trimText } from "@v2/helpers/global";
import { uiColors } from "@v2/lib/color";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { preload } from 'react-dom';
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import SliderHeader, { SliderHeaderSettings } from "./sliderHeader";
import LandingPageComponentLoader from "../landingPageComponentLoader";
import ProductCardBody, { LandingPageLiveData } from "./productCardBody";
import { LandingPageComponent } from "../types";



export interface LandingPageSliderContent {
    data: LandingPageSlideItem[];
    settings: LandingPageSliderSettings;
}

export interface LandingPageSliderSettings {
    link: string | null;
    label: string | null;
    title: string | null;
    header?: SliderHeaderSettings | null;
    full_width: boolean;
    object_fit: "contain" | "cover"

    slier_options: {
        gap: string;
        dots: boolean;
        loop: boolean;
        autoplay: boolean;
        navigation: boolean;
        navigation_position?: 'header' | 'middle';
        // Where the dots sit. 'over' floats them on the last slide (the old and
        // still default behaviour); 'below' reserves a strip under the slides.
        pagination_position?: 'over' | 'below' | null;
        // Distance from the bottom edge in 'over' mode, extra breathing room in
        // 'below' mode. 0 keeps the defaults.
        pagination_offset?: number | string | null;
        // Space kept clear at both ends of the track, per viewport. Absent on
        // every slider written before this existed, hence optional.
        edge_offset?: {
            mobile?: number | string | null;
            tablet?: number | string | null;
            desktop?: number | string | null;
        } | null;
        slides_per_view: {
            mobile: {
                count: number | "auto" | string;
                ratio: string;
            };
            tablet: {
                count: number | "auto" | string;
                ratio: string;
            };
            desktop: {
                count: number | "auto" | string;
                ratio: string;
            };
        };
    };

    background_color: string | null;
    item_background_color: string | null;
}

export default function SliderLC({ content }: {
    content: LandingPageSliderContent;
}) {
    const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null);
    const [mounted, setMounted] = useState(false);
    const [initialSlide, setInitialSlide] = useState(0);
    const pathname = usePathname();
    const headerSettings = getSliderHeaderSettings(content.settings);
    const slidesPerView = content.settings.slier_options.slides_per_view;
    const mobileSlidesPerView = normalizeSlidesPerViewCount(slidesPerView.mobile.count);
    const tabletSlidesPerView = normalizeSlidesPerViewCount(slidesPerView.tablet.count);
    const desktopSlidesPerView = normalizeSlidesPerViewCount(slidesPerView.desktop.count);
    const edgeOffset = {
        mobile: normalizeEdgeOffset(content.settings.slier_options.edge_offset?.mobile),
        tablet: normalizeEdgeOffset(content.settings.slier_options.edge_offset?.tablet),
        desktop: normalizeEdgeOffset(content.settings.slier_options.edge_offset?.desktop),
    };
    const paginationOffset = normalizeEdgeOffset(content.settings.slier_options.pagination_offset);
    const dotsBelow = content.settings.slier_options.dots
        && content.settings.slier_options.pagination_position === 'below';
    const navigationPosition = getNavigationPosition(content.settings);
    const showHeaderNavigation = content.settings.slier_options.navigation && navigationPosition === 'header';
    const showMiddleNavigation = content.settings.slier_options.navigation && navigationPosition === 'middle';
    const isHeroSlider = desktopSlidesPerView === 1;
    const [activeIndex, setActiveIndex] = useState(0);
    // Swiper moves slides with a transform, and the browser's own lazy loading judges a
    // slide by its untransformed position -- so every off-screen slide downloads anyway.
    // The window below decides it here instead: the slides actually sharing the viewport,
    // plus one on each side so a swipe never lands on an empty box.
    // Swiper resolves slidesPerView per breakpoint, so ask it rather than taking the
    // widest of the three: a phone showing one card should not load as if it were the
    // desktop grid. The max is only the first-render fallback, before Swiper exists.
    const [slidesInView, setSlidesInView] = useState(() => Math.max(
        countSlidesInView(mobileSlidesPerView),
        countSlidesInView(tabletSlidesPerView),
        countSlidesInView(desktopSlidesPerView),
    ));
    const hasComponentSlides = useMemo(() => content.data.some(i => i.source === 'component'), [content.data]);
    // The skeleton draws image-shaped boxes from the slide ratio, which says
    // nothing about a component slide's height.
    const showSkeleton = !mounted && !isHeroSlider && !hasComponentSlides;
    const visibleSlides = useMemo(() => content.data.filter(i => i.show_in_site), [content.data]);
    const shouldPersistSlide = visibleSlides.some(slide => slide.source === 'product' || slide.source === 'site_product');
    const sliderStorageKey = useMemo(
        () => shouldPersistSlide ? buildSliderStorageKey(pathname, content, visibleSlides) : null,
        [content, pathname, shouldPersistSlide, visibleSlides]
    );

    useEffect(() => {
        setInitialSlide(readStoredSliderIndex(sliderStorageKey, visibleSlides.length));
        setMounted(true);
    }, [sliderStorageKey, visibleSlides.length]);

    return (
        <Box sx={{
            width: 1,
            maxWidth: content.settings.full_width ? 'none' : 1400,
            bgcolor: content.settings.background_color || 'transparent',
            margin: 'auto',
            px: content.settings.full_width ? 0 : { xs: 2, lg2: 0 },
            // A card slider lets its cards run to the screen edge below lg (see
            // below), so the slider itself is what cuts them off there.
            overflowX: content.settings.full_width || !isHeroSlider ? 'clip' : undefined,
            userSelect: 'none',
            // Swiper gives every slide the height of the tallest one. An image
            // slide wants that; a component slide has to size itself.
            ...(hasComponentSlides && {
                '& .swiper-wrapper': { alignItems: 'stretch' },
                '& .swiper-slide': { height: 'auto' },
            }),
            '& .swiper-pagination': {
                // 'below' puts the dots in the strip reserved on the Swiper
                // itself, so they land under the card instead of on top of its
                // title — which is what a full-width slider used to do.
                bottom: dotsBelow
                    ? 0
                    : (paginationOffset || (content.settings.full_width ? { xs: 16, md: 8 } : 4)),
                '& .swiper-pagination-bullet': {
                    borderRadius: 2,
                    transition: '250ms !important',
                    background: colors.grey[500],
                    width: 6,
                    height: 6,

                },
                '& .swiper-pagination-bullet-active': {
                    background: uiColors.primary,
                    // width: 24,
                }
            }
        }}>
            <SliderHeader
                settings={headerSettings}
                showNavigation={showHeaderNavigation}
                onPrev={() => swiperRef?.slidePrev()}
                onNext={() => swiperRef?.slideNext()}
            />
            {showSkeleton ? (
                <SliderSkeleton
                    gap={content.settings.slier_options.gap}
                    ratios={{
                        mobile: slidesPerView.mobile.ratio,
                        tablet: slidesPerView.tablet.ratio,
                        desktop: slidesPerView.desktop.ratio,
                    }}
                    slidesPerView={{
                        mobile: mobileSlidesPerView,
                        tablet: tabletSlidesPerView,
                        desktop: desktopSlidesPerView,
                    }}
                    edgeOffset={edgeOffset}
                />
            ) : (
                <Box sx={!isHeroSlider && !content.settings.full_width ? {
                    // Below lg a card row runs past the gutter to the screen edge
                    // instead of stopping 16px short of it.
                    overflow: { xs: 'visible', lg: 'hidden' },
                    '& > .swiper': { overflow: { xs: 'visible', lg: 'hidden' } },
                } : {}}>
                    <Swiper
                        style={{
                            paddingBottom: dotsBelow
                                ? `${28 + paginationOffset}px`
                                : (!isHeroSlider && !content.settings.full_width && content.settings.slier_options.dots
                                    ? '28px'
                                    : undefined),
                        }}
                        // dir="rtl"
                        // observer={true}
                        // observeParents={true}
                        // resizeObserver={true}
                        // watchOverflow={false}
                        // centerInsufficientSlides={true}
                        // slidesPerView={content.settings.slier_options.slides_per_view.desktop}
                        slidesPerView={mobileSlidesPerView}
                        spaceBetween={parseInt(content.settings.slier_options.gap)}
                        // The offset goes on the track, not on the container: a
                        // padded container would also stop the slides scrolling
                        // past it, which is the whole point of the setting.
                        slidesOffsetBefore={edgeOffset.mobile}
                        slidesOffsetAfter={edgeOffset.mobile}
                        breakpoints={{
                            0: {
                                slidesPerView: mobileSlidesPerView,
                                slidesOffsetBefore: edgeOffset.mobile,
                                slidesOffsetAfter: edgeOffset.mobile,
                            },
                            768: {
                                slidesPerView: tabletSlidesPerView,
                                slidesOffsetBefore: edgeOffset.tablet,
                                slidesOffsetAfter: edgeOffset.tablet,
                            },
                            1280: {
                                slidesPerView: desktopSlidesPerView,
                                slidesOffsetBefore: edgeOffset.desktop,
                                slidesOffsetAfter: edgeOffset.desktop,
                            },
                        }}
                        modules={[Pagination, Autoplay]}
                        speed={450}
                        initialSlide={initialSlide}
                        onSwiper={(swiper) => {
                            setSwiperRef(swiper);
                            restoreStoredSliderIndex(swiper, sliderStorageKey, visibleSlides.length);
                            setActiveIndex(swiper.realIndex);
                            setSlidesInView(countSlidesInView(swiper.params.slidesPerView as number | "auto"));
                        }}
                        onSlideChange={(swiper) => {
                            storeSliderIndex(sliderStorageKey, swiper.realIndex);
                            setActiveIndex(swiper.realIndex);
                        }}
                        onBreakpoint={(swiper) => {
                            setSlidesInView(countSlidesInView(swiper.params.slidesPerView as number | "auto"));
                        }}

                        autoplay={content.settings.slier_options.autoplay ? {
                            delay: 8000,
                            pauseOnMouseEnter: true
                        } : false}
                        // onAutoplayTimeLeft={onAutoplayTimeLeft}
                        pagination={{
                            enabled: content.settings.slier_options.dots,
                            dynamicBullets: true,
                            dynamicMainBullets: 3,
                        }}
                        loop={content.settings.slier_options.loop === true}
                    >
                        {showMiddleNavigation && (
                            <SliderMiddleNavigation
                                onPrev={() => swiperRef?.slidePrev()}
                                onNext={() => swiperRef?.slideNext()}
                            />
                        )}
                        {visibleSlides.map((slide, index) => {
                            // A component slide carries a whole nested tree instead of an
                            // image, so none of the image/product chrome below applies.
                            if (slide.source === 'component') {
                                return (
                                    <SwiperSlide key={slide.id}>
                                        <Box sx={{
                                            height: 1,
                                            bgcolor: content.settings.item_background_color || 'transparent',
                                        }}>
                                            <LandingPageComponentLoader
                                                components={slide.components ?? []}
                                                nested
                                                containerSx={{ display: 'contents' }}
                                            />
                                        </Box>
                                    </SwiperSlide>
                                );
                            }

                            const isProduct = slide.source === 'product' || slide.source === 'site_product';
                            const isBlog = slide.source === 'blog';
                            const discountPercent = isProduct && slide.live_data?.rial_price
                                ? Math.round((slide.live_data.discount / slide.live_data.rial_price) * 100)
                                : 0;
                            return (
                                <SwiperSlide key={slide.id}>
                                    <Box sx={{
                                        display: 'block',
                                        bgcolor: content.settings.item_background_color || (isBlog ? '#fff' : 'transparent'),
                                        ...(isProduct && {
                                            border: '1px solid #e9e7e3',
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 18px rgba(40,35,25,.06)',
                                        }),
                                        ...(isBlog && {
                                            border: '1px solid #ededed',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            transition: 'box-shadow .18s, transform .18s',
                                            '&:hover': { boxShadow: '0 14px 30px rgba(0,0,0,.09)', transform: 'translateY(-4px)' },
                                        }),
                                    }} {...(slide.link ? {
                                        component: Link,
                                        href: slide.link,
                                    } : {})}>
                                        <Box className="slide-img-wrap" sx={{ position: 'relative' }}>
                                            <SliderImage
                                                objectFit={content.settings.object_fit}
                                                ratio={{
                                                    mobile: content.settings.slier_options.slides_per_view.mobile.ratio,
                                                    tablet: content.settings.slier_options.slides_per_view.tablet.ratio,
                                                    desktop: content.settings.slier_options.slides_per_view.desktop.ratio,
                                                }}
                                                source={slide.source}
                                                // desktop_image is the photo the landing was built with, which
                                                // is a snapshot of whichever variant was current then. live_data
                                                // carries the variant the price and the link resolve to now, so
                                                // it wins whenever the backend could resolve one.
                                                desktopId={slide.live_data?.media_id ?? slide.desktop_image}
                                                mobileId={slide.live_data?.media_id ?? slide.mobile_image}
                                                // Only the one-slide-per-view hero is above the fold.
                                                // A card slider's first slide is not, and eager-loading
                                                // it put it in front of the real LCP image.
                                                priority={isHeroSlider && index === 0}
                                                load={isSlideWithinLoadWindow(index, activeIndex, slidesInView, visibleSlides.length, content.settings.slier_options.loop === true)}
                                                fullWidth={content.settings.full_width}
                                                slidesPerView={{ mobile: mobileSlidesPerView, desktop: desktopSlidesPerView }}
                                                gap={content.settings.slier_options.gap}
                                                background={isProduct ? 'radial-gradient(circle at 50% 40%, #f9f9f8 0%, #efefed 100%)' : undefined}
                                                blendMode={isProduct ? 'multiply' : undefined}
                                            />
                                            {isProduct && slide.live_data?.tag && (
                                                <Box sx={{
                                                    position: 'absolute', top: 12, left: 12, zIndex: 2,
                                                    bgcolor: '#1a1a1a', color: '#fff',
                                                    fontSize: 11, fontWeight: 700,
                                                    px: 1.5, py: 0.5, borderRadius: 99,
                                                }}>
                                                    {slide.live_data.tag}
                                                </Box>
                                            )}
                                            {isProduct && !!discountPercent && (
                                                <Box sx={{
                                                    position: 'absolute', top: 12, right: 12, zIndex: 2,
                                                    bgcolor: '#bb202e', color: '#fff',
                                                    fontSize: 12, fontWeight: 800,
                                                    px: 1, py: 0.5, borderRadius: 1,
                                                }}>
                                                    %{discountPercent}-
                                                </Box>
                                            )}
                                        </Box>
                                        {isProduct && slide.live_data && <ProductCardBody liveData={slide.live_data} />}
                                        {['blog', 'none'].includes(slide.source) && <SlideOfDefaultData slide={slide} />}
                                    </Box>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </Box>
            )}
        </Box>
    );
};

// One number per viewport, applied to both ends. Asymmetric offsets are not
// offered on purpose: in RTL "before" is the right-hand side, and a start/end
// pair is read backwards by everyone who writes the JSON.
function normalizeEdgeOffset(value: number | string | null | undefined): number {
    const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
    if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed < 0) return 0;
    return parsed;
}

function getNavigationPosition(settings: LandingPageSliderSettings): 'header' | 'middle' {
    if (settings.slier_options.navigation_position) return settings.slier_options.navigation_position;
    const desktopCount = normalizeSlidesPerViewCount(settings.slier_options.slides_per_view.desktop.count);
    return desktopCount === 1 ? 'middle' : 'header';
}

function normalizeSlidesPerViewCount(count: number | "auto" | string): number | "auto" {
    if (count === 'auto') return 'auto';
    const parsed = Number(count);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function numericSlidesPerView(count: number | "auto"): number {
    return count === 'auto' ? 1 : count;
}

function getSliderHeaderSettings(settings: LandingPageSliderSettings): SliderHeaderSettings {
    return {
        label: settings.header?.label ?? null,
        title: settings.header?.title ?? settings.title ?? '',
        subtitle: settings.header?.subtitle ?? null,
        show_accent: true,
        align: 'right',
        link_text: settings.header?.link_text ?? (settings.link ? 'مشاهده بیشتر' : null),
        link_url: settings.header?.link_url ?? settings.link ?? null,
    };
}

const SliderMiddleNavigation = ({ onPrev, onNext }: {
    onPrev: () => void;
    onNext: () => void;
}) => (
    <>
        <SliderFloatingNavButton
            aria-label="اسلاید قبلی"
            onClick={onPrev}
            sx={{ right: 16 }}
        >
            <ChevronLeft size={20} />
        </SliderFloatingNavButton>
        <SliderFloatingNavButton
            aria-label="اسلاید بعدی"
            onClick={onNext}
            sx={{ left: 16 }}
        >
            <ChevronRight size={20} />
        </SliderFloatingNavButton>
    </>
);

const SliderFloatingNavButton = ({ children, onClick, sx, 'aria-label': ariaLabel }: {
    children: ReactNode;
    onClick: () => void;
    sx: BoxProps['sx'];
    'aria-label': string;
}) => (
    <IconButton
        aria-label={ariaLabel}
        onClick={onClick}
        sx={{
            position: 'absolute',
            zIndex: 2,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 42,
            height: 42,
            border: `1px solid ${colors.grey[300]}`,
            bgcolor: '#ffffff',
            color: '#111',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': { bgcolor: colors.grey[50] },
            ...sx,
        }}
    >
        {children}
    </IconButton>
);

const SliderSkeleton = ({ gap, ratios, slidesPerView, edgeOffset }: {
    gap: string;
    ratios: { mobile: string; tablet: string; desktop: string };
    slidesPerView: { mobile: number | "auto"; tablet: number | "auto"; desktop: number | "auto" };
    edgeOffset: { mobile: number; tablet: number; desktop: number };
}) => {
    const gapValue = parseInt(gap || '16');
    const mobileCount = numericSlidesPerView(slidesPerView.mobile);
    const tabletCount = numericSlidesPerView(slidesPerView.tablet);
    const desktopCount = numericSlidesPerView(slidesPerView.desktop);
    const itemCount = Math.max(Math.ceil(mobileCount), Math.ceil(tabletCount), Math.ceil(desktopCount));

    return (
        <Box sx={{
            display: 'flex',
            gap: `${gapValue}px`,
            overflow: 'hidden',
            width: '100%',
            // Mirror the track offset, or the slider shifts sideways the moment
            // the real Swiper replaces this.
            paddingInline: {
                xs: `${edgeOffset.mobile}px`,
                md: `${edgeOffset.tablet}px`,
                lg: `${edgeOffset.desktop}px`,
            },
        }}>
            {Array.from({ length: itemCount }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        display: {
                            xs: index < Math.ceil(mobileCount) ? 'block' : 'none',
                            md: index < Math.ceil(tabletCount) ? 'block' : 'none',
                            lg: index < Math.ceil(desktopCount) ? 'block' : 'none',
                        },
                        flex: '0 0 auto',
                        width: {
                            xs: getSkeletonSlideWidth(mobileCount, gapValue),
                            md: getSkeletonSlideWidth(tabletCount, gapValue),
                            lg: getSkeletonSlideWidth(desktopCount, gapValue),
                        },
                        aspectRatio: {
                            xs: ratios.mobile || '1 / 1',
                            md: ratios.tablet || ratios.mobile || '1 / 1',
                            lg: ratios.desktop || ratios.tablet || '3 / 1',
                        },
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        sx={{
                            width: '100%',
                            height: '100%',
                            transform: 'none',
                            borderRadius: 1,
                        }}
                    />
                </Box>
            ))}
        </Box>
    );
};

function getSkeletonSlideWidth(count: number, gap: number) {
    return `calc((100% - ${(count - 1) * gap}px) / ${count})`;
}

const SlideOfDefaultData = ({ slide }: {
    slide: LandingPageSlideItem
}) => {
    if (slide.source === 'blog') return <SlideOfBlog slide={slide} />;

    if (!slide.title && !slide.subtitle) return null;
    return (
        <Box sx={{ p: 1, pt: 1, userSelect: 'none' }}>
            {slide.title && (
                <TextOverflow sx={{ fontSize: 15, fontWeight: 700, color: colors.grey[900] }}>
                    {slide.title}
                </TextOverflow>
            )}
            {slide.subtitle && (
                <TextOverflow className="en-num" sx={{ color: colors.grey[600], fontSize: 13, mt: 1 }}>
                    {trimText(slide.subtitle, 60)}
                </TextOverflow>
            )}
        </Box>
    );
}

const SlideOfBlog = ({ slide }: { slide: LandingPageComponentItemBlog }) => {
    if (!slide.title) return null;
    return (
        <Box sx={{ p: { xs: 1.5, sm2: 2, lg: 2.5 }, userSelect: 'none' }}>
            {slide.extra?.category_name && (
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#bb202e' }}>
                    {slide.extra.category_name}
                </Typography>
            )}
            <Box sx={{
                fontSize: { xs: 14, sm2: 15, lg: 15.5 },
                fontWeight: 500,
                lineHeight: 1.5,
                color: '#1f1f1f',
                mt: slide.extra?.category_name ? '8px' : 0,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: '42px',
            }}>
                {slide.title}
            </Box>
            {slide.extra?.post_date && (
                <Typography sx={{ fontSize: { xs: 11, sm2: 12 }, mt: '12px', color: '#a0a0a5' }}>
                    {calcFromNow(slide.extra.post_date)}
                </Typography>
            )}
        </Box>
    );
}

const SliderImage: FC<{
    desktopId: string | null,
    mobileId: string | null,
    link?: string,
    priority?: boolean,
    // False while the slide is far enough from the viewport that its image is not worth
    // fetching yet. The box still renders, so nothing shifts when it arrives.
    load?: boolean,
    objectFit?: LandingPageSliderSettings['object_fit']
    source: LandingPageComponentSource;
    fullWidth: boolean;
    gap: string;
    background?: string;
    blendMode?: string;
    slidesPerView: {
        mobile: number | "auto";
        desktop: number | "auto";
    };
    ratio: {
        mobile: string
        tablet: string
        desktop: string
    }
} & BoxProps> = ({ ratio, objectFit, source, desktopId, mobileId, priority, load = true, fullWidth, slidesPerView, gap, background, blendMode, ...restProps }) => {
    const isProductSource = source === 'product' || source === 'site_product';
    const useViewportWidth = fullWidth && slidesPerView.desktop === 1;
    const mobileSize = getSliderImageSize({
        containerWidth: 430,
        count: slidesPerView.mobile,
        gap,
        ratio: ratio.mobile,
        imageRatio: isProductSource ? PRODUCT_SLIDER_IMAGE_RATIO : undefined,
    });
    const desktopSize = getSliderImageSize({
        containerWidth: fullWidth ? 1920 : 1400,
        count: slidesPerView.desktop,
        gap,
        ratio: ratio.desktop,
        imageRatio: isProductSource ? PRODUCT_SLIDER_IMAGE_RATIO : undefined,
    });
    const mobileMediaId = mobileId || desktopId;
    const desktopMediaId = desktopId || mobileId;
    const mobileRatio = isProductSource ? PRODUCT_SLIDER_IMAGE_RATIO : parseRatio(ratio.mobile);
    const desktopRatio = isProductSource ? PRODUCT_SLIDER_IMAGE_RATIO : parseRatio(ratio.desktop);
    const mobileSrc = buildSliderImageSrc(mobileMediaId, mobileSize, objectFit, isProductSource);
    const desktopSrc = buildSliderImageSrc(desktopMediaId, desktopSize, objectFit, isProductSource);
    const mobileSrcSet = buildSliderSrcSet(mobileMediaId, mobileSize.width, mobileRatio, objectFit, isProductSource);
    const desktopSrcSet = buildSliderSrcSet(desktopMediaId, desktopSize.width, desktopRatio, objectFit, isProductSource);
    const mobileSizes = useViewportWidth ? '100vw' : `${mobileSize.width}px`;
    const desktopSizes = useViewportWidth ? '100vw' : `${desktopSize.width}px`;

    if (!mobileSrc && !desktopSrc) return null;

    // The slider only renders on the client, so nothing in the initial HTML tells the
    // preload scanner about the hero. next/image's `priority` used to inject these at
    // hydration; `media` keeps it to the one variant this viewport will actually show.
    if (priority && load) {
        if (desktopSrc) {
            preload(desktopSrc, {
                as: 'image',
                fetchPriority: 'high',
                media: `(min-width: ${SLIDER_DESKTOP_MIN}px)`,
                ...(desktopSrcSet ? { imageSrcSet: desktopSrcSet, imageSizes: desktopSizes } : {}),
            });
        }
        if (mobileSrc) {
            preload(mobileSrc, {
                as: 'image',
                fetchPriority: 'high',
                media: `(max-width: ${SLIDER_DESKTOP_MIN - 1}px)`,
                ...(mobileSrcSet ? { imageSrcSet: mobileSrcSet, imageSizes: mobileSizes } : {}),
            });
        }
    }

    // One element rather than a mobile box and a desktop box toggled with `display`.
    // Both were in the DOM, so the browser downloaded both — and on a hero slide that
    // meant the unused one competing for bandwidth with the LCP image. A <picture>
    // lets the browser resolve the art direction before it fetches anything.
    return (
        <Box sx={{
            width: useViewportWidth ? '100vw' : '100%',
            maxWidth: useViewportWidth ? '100vw' : undefined,
            aspectRatio: { xs: ratio.mobile ?? '1 / 1', lg: ratio.desktop ?? '3 / 1' },
            position: 'relative',
            background: background,
            '& picture': { display: 'contents' },
            '& img': { position: 'absolute', inset: 0, width: '100%', height: '100%' },
        }} {...restProps}>
            {load && <picture>
                {desktopSrc && (
                    <source media={`(min-width: ${SLIDER_DESKTOP_MIN}px)`} srcSet={desktopSrcSet ?? desktopSrc} sizes={desktopSizes} />
                )}
                <img
                    src={mobileSrc ?? desktopSrc ?? undefined}
                    srcSet={mobileSrcSet}
                    sizes={mobileSizes}
                    alt="slider"
                    loading={priority ? 'eager' : 'lazy'}
                    fetchPriority={priority ? 'high' : 'low'}
                    decoding={priority ? 'sync' : 'async'}
                    style={{ objectFit: objectFit, mixBlendMode: blendMode as any }}
                />
            </picture>}
        </Box>
    );
};

function getSliderImageSize({ containerWidth, count, gap, ratio, imageRatio }: {
    containerWidth: number;
    count: number | "auto";
    gap: string;
    ratio: string;
    imageRatio?: number;
}) {
    const numericCount = numericSlidesPerView(count);
    const gapValue = parseInt(gap || '0');
    // The CSS width the slide is drawn at. It used to be multiplied by 1.75 to land
    // between a 1x and a 2x screen, because a single `src` cannot serve both. The
    // srcset below covers that properly, so this stays the honest layout width.
    const width = Math.max(1, Math.round((containerWidth - (numericCount - 1) * gapValue) / numericCount));
    const height = Math.max(1, Math.round(width / (imageRatio ?? parseRatio(ratio))));
    return { width, height };
}

function parseRatio(ratio: string) {
    const [rawWidth, rawHeight] = String(ratio || '1 / 1').split('/').map(part => Number(part.trim()));
    if (!Number.isFinite(rawWidth) || !Number.isFinite(rawHeight) || rawWidth <= 0 || rawHeight <= 0) return 1;
    return rawWidth / rawHeight;
}

const PRODUCT_SLIDER_IMAGE_RATIO = 1.618;

// The active slide, everything sharing the viewport with it, and one more on each side.
const SLIDE_LOAD_AHEAD = 1;

function countSlidesInView(value: number | "auto") {
    // "auto" sizes slides from their content, so there is no count to read. Four covers
    // the widest card layout this slider is used with.
    return typeof value === "number" && Number.isFinite(value) ? Math.ceil(value) : 4;
}

function isSlideWithinLoadWindow(index: number, activeIndex: number, slidesInView: number, total: number, loop: boolean) {
    const start = activeIndex - SLIDE_LOAD_AHEAD;
    const end = activeIndex + slidesInView - 1 + SLIDE_LOAD_AHEAD;
    if (index >= start && index <= end) return true;
    // A looping track wraps, so the window can reach round either end.
    if (!loop || total <= 0) return false;
    return [index - total, index + total].some(wrapped => wrapped >= start && wrapped <= end);
}

const SLIDER_DESKTOP_MIN = 1200;
const SLIDER_MAX_IMAGE_WIDTH = 1920;
const SLIDER_DENSITIES = [1, 1.5, 2];

// One entry per density the media server can actually satisfy, so the browser picks by
// screen and viewport instead of every visitor sharing one compromise width.
function buildSliderSrcSet(mediaId: string | null, cssWidth: number, imageRatio: number, objectFit?: LandingPageSliderSettings['object_fit'], forceCrop = false) {
    if (!mediaId) return undefined;
    const widths = [...new Set(SLIDER_DENSITIES.map(density => Math.min(Math.round(cssWidth * density), SLIDER_MAX_IMAGE_WIDTH)))].sort((a, b) => a - b);
    return widths
        .map(width => `${buildSliderImageSrc(mediaId, { width, height: Math.max(1, Math.round(width / imageRatio)) }, objectFit, forceCrop)} ${width}w`)
        .join(', ');
}

function buildSliderImageSrc(mediaId: string | null, size: { width: number; height: number }, objectFit?: LandingPageSliderSettings['object_fit'], forceCrop = false) {
    if (!mediaId) return null;
    const params = new URLSearchParams({
        action: forceCrop || objectFit === 'cover' ? 'cropThumbnail' : 'thumbnail',
        width: String(size.width),
        height: String(size.height),
        quality: forceCrop ? '86' : '84',
    });
    return `${process.env.NEXT_PUBLIC_MEDIA_URL}/api/v1/media/${mediaId}?${params.toString()}`;
}

function buildSliderStorageKey(pathname: string, content: LandingPageSliderContent, visibleSlides: LandingPageSlideItem[]) {
    const slideKey = visibleSlides
        .map(slide => `${slide.source}:${slide.source_id || slide.id}`)
        .join(',');

    return [
        'landing-product-slider',
        pathname,
        content.settings.title ?? '',
        content.settings.link ?? '',
        slideKey,
    ].join('|');
}

function readStoredSliderIndex(storageKey: string | null, slideCount: number) {
    if (!storageKey || slideCount <= 0 || typeof window === 'undefined') return 0;

    try {
        const index = Number(sessionStorage.getItem(storageKey) ?? 0);
        return Number.isFinite(index) ? Math.min(Math.max(index, 0), slideCount - 1) : 0;
    } catch {
        return 0;
    }
}

function storeSliderIndex(storageKey: string | null, index: number) {
    if (!storageKey || typeof window === 'undefined') return;

    try {
        sessionStorage.setItem(storageKey, String(index));
    } catch {
        // sessionStorage can be unavailable in private or constrained contexts.
    }
}

function restoreStoredSliderIndex(swiper: SwiperType, storageKey: string | null, slideCount: number) {
    const index = readStoredSliderIndex(storageKey, slideCount);
    if (!index) return;

    if (swiper.params.loop) {
        swiper.slideToLoop(index, 0, false);
        return;
    }

    swiper.slideTo(index, 0, false);
}

type LandingPageSlideItem =
    | LandingPageComponentItemNone
    | LandingPageComponentItemSiteProduct
    | LandingPageComponentItemProduct
    | LandingPageComponentItemBlog
    | LandingPageComponentItemComponent;

type LandingPageComponentSource = "none" | "site_product" | "product" | "blog" | "component";


// Extra data is only for blog
interface LandingPageBlogExtra {
    image_alt: string;
    post_date: string;
    category_name?: string | null;
    excerpt?: string | null;
}

// ----------------------
// Base Component Item
// ----------------------

interface LandingPageComponentItemBase {
    id: number;
    title: string;
    link: string | null;
    subtitle: string | null;
    source_id: number;
    source: LandingPageComponentSource;
    mobile_image: string | null;
    desktop_image: string | null;
    show_in_site: boolean;
}

// ----------------------
// Variants
// ----------------------

// 1) source = none
interface LandingPageComponentItemNone extends LandingPageComponentItemBase {
    source: "none";
    live_data?: undefined;
    extra?: undefined;
}

// 2) source = site_product
interface LandingPageComponentItemSiteProduct extends LandingPageComponentItemBase {
    source: "site_product";
    live_data: LandingPageLiveData;
    extra?: undefined;
}

// 3) source = product
interface LandingPageComponentItemProduct extends LandingPageComponentItemBase {
    source: "product";
    live_data: LandingPageLiveData;
    extra?: undefined;
}

// 4) source = blog
interface LandingPageComponentItemBlog extends LandingPageComponentItemBase {
    source: "blog";
    live_data?: undefined;
    extra: LandingPageBlogExtra;
}

// 5) source = component — the slide is a nested landing component tree, most
// often a single block. Images, prices and ratios do not apply to it.
interface LandingPageComponentItemComponent extends LandingPageComponentItemBase {
    source: "component";
    components: LandingPageComponent[];
    live_data?: undefined;
    extra?: undefined;
}
